// Enhanced API with error handling
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5002/api',
  timeout: 30000,
  withCredentials: true
});

let isRefreshing = false;

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('ims_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const { response, request, code, config } = error;

    // Network error (no response)
    if (!response) {
      if (code === 'ECONNABORTED') {
        enqueueSnackbar('Request timeout. Please try again.', { variant: 'error' });
      } else if (request) {
        enqueueSnackbar('Server not reachable. Check your connection.', { variant: 'error' });
      } else {
        enqueueSnackbar('Network error. Please check your connection.', { variant: 'error' });
      }
      return Promise.reject(error);
    }

    const { status, data } = response;

    // Handle specific status codes
    switch (status) {
      case 401:
        // Skip token refresh for auth endpoints (login, register, etc.)
        if (config.url && (config.url.includes('/auth/login') || config.url.includes('/auth/register') || config.url.includes('/auth/send-otp') || config.url.includes('/auth/login-otp'))) {
          break;
        }
        // Try to refresh token for authenticated requests
        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const token = localStorage.getItem('ims_token');
            const refreshResponse = await axios.post(
              `${process.env.REACT_APP_API_URL || 'http://localhost:5002/api'}/auth/refresh`,
              {},
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            
            if (refreshResponse.data.token) {
              localStorage.setItem('ims_token', refreshResponse.data.token);
              localStorage.setItem('ims_user', JSON.stringify(refreshResponse.data.user));
              config.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
              isRefreshing = false;
              return api.request(config);
            }
          } catch (refreshError) {
            isRefreshing = false;
            enqueueSnackbar('Session expired. Please login again.', { variant: 'error' });
            localStorage.removeItem('ims_token');
            localStorage.removeItem('ims_user');
            setTimeout(() => {
              window.location.href = '/login';
            }, 2000);
            return Promise.reject(error);
          }
        }
        break;
      
      case 403:
        enqueueSnackbar('Access denied. You don\'t have permission.', { variant: 'error' });
        break;
      
      case 404:
        enqueueSnackbar('Resource not found.', { variant: 'error' });
        break;
      
      case 422:
        enqueueSnackbar(data?.message || 'Validation error.', { variant: 'error' });
        break;
      
      case 429:
        enqueueSnackbar('Too many requests. Please try again later.', { variant: 'error' });
        break;
      
      case 500:
        enqueueSnackbar('Server error. Please try again later.', { variant: 'error' });
        break;
      
      default:
        enqueueSnackbar(data?.message || 'Something went wrong.', { variant: 'error' });
        break;
    }

    return Promise.reject(error);
  }
);

export default api;
